import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { STORAGE_KEY_BIRTH_CHART_NUDGE_PREFIX } from '../../../../const';
import { getUser } from '../../../../store/slices/authSlice';
import {
    FLOW_SOURCE,
    FLOW_STEP,
    closeBirthChartFlow,
    getBirthChartFlow,
    patchFlowDetails,
    prefillFromUser,
    setFlowStep
} from '../../../../store/slices/birthChartSlice';
import { gaEvents } from '../../../../utils/analytics';
import DateSheet from './DateSheet';
import HeroModal from './HeroModal';
import LoadingStep from './LoadingStep';
import PlaceSheet from './PlaceSheet';
import TimeSheet from './TimeSheet';
import VerifySheet from './VerifySheet';
import { firstMissingStep, prefillDetails } from './flowLogic';

/**
 * Birth Chart flow (HORO-10360) — one global state machine, mounted once in
 * App. Renders the step named by `state.birthChart.flow.step`; all
 * transitions are Redux actions, so any entry point can open it.
 */
const BirthChartFlow = () => {
    const dispatch = useDispatch();
    const flow = useSelector(getBirthChartFlow);
    const user = useSelector(getUser);
    const startedTracked = useRef(false);

    const { open, step } = flow;

    // Esc closes — except mid-generation.
    useEffect(() => {
        if (!open || step === FLOW_STEP.LOADING) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') dispatch(closeBirthChartFlow());
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, step, dispatch]);

    useEffect(() => {
        if (!open) startedTracked.current = false;
    }, [open]);

    if (!open) return null;

    const close = () => dispatch(closeBirthChartFlow());

    // Confirming the intro/offer modal: fill whatever the profile already knows (date,
    // time, place) so the person only enters what is missing, then start at the first
    // missing field — or straight at Verify to confirm everything when nothing is.
    const handleConfirm = () => {
        if (!startedTracked.current) {
            startedTracked.current = true;
            gaEvents.bcFlowStarted(flow.source);
        }
        const details = prefillDetails(flow.details, prefillFromUser(user));
        dispatch(patchFlowDetails(details));
        dispatch(setFlowStep(firstMissingStep(details)));
    };

    // The X on the intro/offer modal is the only way to turn the chart down.
    const handleReject = () => {
        gaEvents.bcRejected(flow.source, step);
        if (flow.source === FLOW_SOURCE.NUDGE) {
            try {
                window.localStorage.setItem(`${STORAGE_KEY_BIRTH_CHART_NUDGE_PREFIX}${user?.id}`, String(Date.now()));
            } catch {
                // best-effort
            }
            gaEvents.bcNudgeDismissed();
        }
        close();
    };

    switch (step) {
        case FLOW_STEP.INTRO:
            return <HeroModal onConfirm={handleConfirm} onReject={handleReject} />;
        case FLOW_STEP.OFFER:
            return <HeroModal onConfirm={handleConfirm} onReject={handleReject} />;
        case FLOW_STEP.DATE:
            return <DateSheet />;
        case FLOW_STEP.TIME:
            return <TimeSheet />;
        case FLOW_STEP.PLACE:
            return <PlaceSheet />;
        case FLOW_STEP.VERIFY:
            return <VerifySheet />;
        case FLOW_STEP.LOADING:
            return <LoadingStep />;
        default:
            return null;
    }
};

export default BirthChartFlow;
